import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { logError } from '@cortex/faro';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { useBdDiagrams, useBdGraph } from '../../../hooks/useBasicDesign.js';
import { toggleIn, type BdViewSettings } from '../../../lib/bd-view-settings.js';
import { TabButton, TabGroup } from '../../ui/Tabs/index.js';
import { BdGraph } from './BdGraph.js';
import { BdMermaid } from './BdMermaid.js';
import type { BdArtifact, BdArtifactDef, BdSavedArtifact } from '../../../lib/api/basic-design.js';

/** 図の種類。 @graph-connects none */
export type BdDiagramKind = 'overview' | 'erd' | 'transitions';

/** 図の並び。 @graph-connects none */
export const BD_DIAGRAM_KINDS: readonly BdDiagramKind[] = ['overview', 'erd', 'transitions'];

/** 新しいタブで開くリンクを、ほかの小さいボタン（secondary / sm）と同じ見た目にする。 @graph-connects none */
const OPEN_LINK_CLASS =
  'inline-flex items-center justify-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-1.5 text-sm font-medium text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)] hover:bg-[var(--color-bg-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]';

export interface BdDiagramsProps {
  /** 基本設計のチケット */
  bdKey: string;
  /** 全節の定義 */
  defs: readonly BdArtifactDef[];
  /** 保存済みの節 */
  saved: Partial<Record<BdArtifact, BdSavedArtifact>>;
  /** 個人の見え方（図の列と向き） */
  settings: BdViewSettings;
  /** 個人の見え方を変える */
  onSettingsChange: (change: (current: BdViewSettings) => BdViewSettings) => void;
  /** 節を開く。`sectionHref` があればそちらを使う */
  onOpenSection?: (artifact: BdArtifact) => void;
  /** 節を開くリンクの先。渡すと選んだ札の行にリンクを出す */
  sectionHref?: (artifact: BdArtifact) => string;
  /** 開いている図。渡すと呼び出し側が持つ（図だけのページは URL に置く） */
  view?: BdDiagramKind;
  /** 図を切り替える（`view` を渡すときに使う） */
  onViewChange?: (view: BdDiagramKind) => void;
  /** 図だけのページで使う。高さいっぱいに描き、新しいタブで開くリンクは出さない */
  fill?: boolean;
}

/** コピーの結果。 @graph-connects none */
type CopyState = 'idle' | 'copied' | 'failed';

/**
 * 基本設計の図。全体図・ER 図・画面遷移図を切り替えて、動く図（`BdGraph`）で読む。
 *
 * 全体図と ER 図は「Mermaid をコピー」で書き出せる（Backlog や文書に貼るため。記述は API の
 * `GET /{bdKey}/diagrams`）。動く図が読めなかったときは、同じ中身の Mermaid を描いて代わりにする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の全体図・ER 図・画面遷移図を切り替えて動く図で読み、全体図と ER 図の Mermaid をコピーする
 * @graph-connects useBdGraph [calls] 動く図の取得
 * @graph-connects useBdDiagrams [calls] Mermaid の取得
 * @graph-connects BdGraph [calls] 動く図の描画
 * @graph-connects BdMermaid [calls] 動く図が読めないときの Mermaid の描画
 */
export function BdDiagrams({
  bdKey,
  defs,
  saved,
  settings,
  onSettingsChange,
  onOpenSection,
  sectionHref,
  view,
  onViewChange,
  fill = false,
}: BdDiagramsProps) {
  const { t } = useTranslation();
  const [ownKind, setOwnKind] = useState<BdDiagramKind>('overview');
  const kind = view ?? ownKind;
  const setKind = (next: BdDiagramKind) => {
    if (onViewChange === undefined) setOwnKind(next);
    else onViewChange(next);
  };
  const graph = useBdGraph(bdKey, true);
  const diagrams = useBdDiagrams(bdKey, true);
  const mermaid = kind === 'transitions' ? null : (diagrams.data?.[kind] ?? null);
  const current = graph.data?.[kind];
  const title = t(`bd.overview.diagrams.${kind}`);

  const header = (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
      <TabGroup ariaLabel={t('bd.overview.diagramsAria')}>
        {BD_DIAGRAM_KINDS.map((k) => (
          <TabButton
            key={k}
            active={kind === k}
            label={t(`bd.overview.diagrams.${k}`)}
            onClick={() => {
              setKind(k);
            }}
          />
        ))}
      </TabGroup>
      <div className="flex flex-wrap items-center gap-2">
        {mermaid === null ? null : <CopyMermaidButton source={mermaid} />}
        {fill ? null : (
          <Link
            to="/projects/bd/$bdKey/graph"
            params={{ bdKey }}
            search={{ view: kind }}
            target="_blank"
            rel="noopener"
            aria-label={t('bd.overview.openInNewTabAria', { title })}
            className={OPEN_LINK_CLASS}
          >
            {t('bd.overview.openInNewTab')}
          </Link>
        )}
      </div>
    </div>
  );
  // 図だけのページで図を描くときは、切り替えとコピーを図の上の操作の枠に入れる
  const headerInGraph =
    fill && graph.error === null && current !== undefined && current.nodes.length > 0;

  return (
    <div className={fill ? 'flex h-full min-h-0 flex-col gap-3' : 'space-y-3'}>
      {headerInGraph ? null : header}
      <div className={fill ? 'min-h-0 flex-1 overflow-auto' : undefined}>
        {graph.error !== null ? (
          mermaid === null ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('bd.overview.diagramsFailed')}
            </p>
          ) : (
            <BdMermaid source={mermaid} label={title} />
          )
        ) : current === undefined ? (
          <p className="text-sm text-[var(--color-text-muted)]">{t('bd.loading')}</p>
        ) : current.nodes.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">{t(`bd.overview.empty.${kind}`)}</p>
        ) : (
          <BdGraph
            // 図を切り替えたら選んだ札と絞り込みを持ち越さない
            key={kind}
            graph={current}
            label={title}
            defs={defs}
            saved={saved}
            hiddenLayers={settings.hiddenLayers}
            direction={settings.direction}
            onToggleLayer={(layer) => {
              onSettingsChange((s) => ({ ...s, hiddenLayers: toggleIn(s.hiddenLayers, layer) }));
            }}
            onDirectionChange={(direction) => {
              onSettingsChange((s) => ({ ...s, direction }));
            }}
            onOpenSection={onOpenSection}
            sectionHref={sectionHref}
            fill={fill}
            toolbar={headerInGraph ? header : null}
          />
        )}
      </div>
    </div>
  );
}

/**
 * 「Mermaid をコピー」。クリップボードが拒んだときは画面に出し、記録にも残す。
 * @graph-connects logError [calls] コピーの失敗の記録
 */
function CopyMermaidButton({ source }: { source: string }) {
  const { t } = useTranslation();
  const [state, setState] = useState<CopyState>('idle');
  return (
    <div className="flex items-center gap-2">
      {state === 'idle' ? null : (
        <span
          role="status"
          className={`text-xs ${state === 'copied' ? 'text-[var(--color-success-text)]' : 'text-[var(--color-error-text)]'}`}
        >
          {state === 'copied' ? t('bd.overview.copied') : t('bd.overview.copyMermaidFailed')}
        </span>
      )}
      <Button
        size="sm"
        variant="secondary"
        onClick={() => {
          setState('idle');
          navigator.clipboard.writeText(source).then(
            () => {
              setState('copied');
            },
            (error: unknown) => {
              logError(error instanceof Error ? error : new Error(String(error)), {
                event: 'bd_mermaid_copy_failed',
              });
              setState('failed');
            },
          );
        }}
      >
        {t('bd.overview.copyMermaid')}
      </Button>
    </div>
  );
}
